import { registerBrick, BrickContext, UIBrick } from 'olympe';
import { useProperty } from '@olympeio/core';
import ReactDOM from 'react-dom';
import React, { useState, useEffect, useRef } from 'react';
import { deleteCrisis, getBulkCrisis, getUserMemberGroups } from './RiskCrisis';
import { filterByValue } from '../section_sf/Utils.js';
import { combineProps, createEvents } from '../shared/_helpers';
import { _theme } from '../shared/_theme';
import { ThemeProvider } from '@mui/material/styles';
import { SuccessErrorPopup as Popup } from '../shared/popups/BasePopup.jsx';
import { getApplicationContext } from '../../common/utils/Context.js';
import InnerAccordion from './InnerAccordion.jsx';

export default class RCMonitorAccordion extends UIBrick {
  /**
   * This method runs when the brick is ready in the HTML DOM.
   * @override
   * @param {!BrickContext} context
   * @param {!Element} elementDom
   */
  draw(context, elementDom) {
    const events = createEvents([
      {
        name: 'throwError',
        event: context.getEvent('Throw Error'),
        before: (event, values) => {
          context.getProperty('Error Message').set(values);
        },
      },
      {
        name: 'reload',
        event: context.getEvent('Reload'),
        before: (event, values) => { },
      },
      {
        name: 'create task',
        event: context.getEvent('Create Task'),
        before: (event, values) => { },
      },
    ]);

    const boolReload = context.getProperty('Boolean reload').observe();

    combineProps([
      {
        name: 'searchText',
        prop: context.getProperty('Search Text'),
        defaultValue: '',
      },
      {
        name: 'isSelectedAll',
        prop: context.getProperty('Selected All'),
        defaultValue: false,
      },
      {
        name: 'crisisDeleted',
        prop: context.getProperty('Crisis Deleted'),
        defaultValue: new Date(),
      },
    ]).subscribe((props) => {
      ReactDOM.render(
        <RCMonitorAccordion.Component
          {...props}
          {...events}
          boolReload={boolReload}
          context={context}
          elementDom={elementDom}
        />,
        elementDom
      );
    });
  }
}

/**
 *
 * @param {Object} props
 * @param {string} props.searchText
 * @return {JSX.Element}
 * @constructor
 */

RCMonitorAccordion.Component = (props) => {
  const [crisisList, setCrisisList] = useState([]);
  const [filteredCrisisList, setFilteredCrisisList] = useState([]);
  const [crisisId, setCrisisId] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showSuccessErrorPopup, setShowSuccessErrorPopup] = useState(false); // controls visibility of success/error popup
  const [errorMessage, setErrorMessage] = useState(''); //error message for Mule api failure
  const firstUpdate = useRef(true);

  const [selectedCrisis, setSelectedCrisis] = useState([]);
  const [checkedState, setCheckedState] = useState(new Array(500).fill(false));
  const refresh = useProperty(props.context, 'triggerRefresh');
  const deleteFromDB = useProperty(props.context, 'deleteFromDB');
  const isAdmin = useProperty(props.context, 'isAdmin');  
  const [memberGroupsList, setMemberGroupList] = useState([]);

  const applicationContext = getApplicationContext(props.context);
  const graphToken = applicationContext.get('graphAPIToken');

  const handleOnChange = (event, position) => {
    const updatedCheckedState = checkedState.map((item, idx) => {
      return idx === position ? !item : item;
    });

    let updatedSelectedCrisis = selectedCrisis;
    let index = updatedSelectedCrisis.indexOf(event.target.id);
    if (index !== -1) {
      updatedCheckedState[position] === false &&
        updatedSelectedCrisis.splice(index, 1);
    } else {
      updatedCheckedState[position] === true &&
        updatedSelectedCrisis.push(event.target.id);
    }
    setSelectedCrisis(updatedSelectedCrisis);
    setCheckedState(updatedCheckedState);
    event.stopPropagation();
  };

  const handleDeleteCrisis = (selection) => {
    let filteredCrisisId = filteredCrisisList.map((crisis) => crisis.crisisID);
    let filteredSelection = selection.filter((id) =>
      filteredCrisisId.includes(id)
    );

    deleteCrisis(filteredSelection)
      .then(() => getCrisisList())
      .then((data) => {
        if (!data.error) {
          setFilteredCrisisList(data);
          setCrisisList(data);
          setCheckedState(new Array(data.length).fill(false));
          setSelectedCrisis([]);
        } else {
          setErrorMessage('Crisis deletion failed!');
        }
        setShowSuccessErrorPopup(true);
      })
      .catch((error) => {
        setErrorMessage(error.message);
        setShowSuccessErrorPopup(true);
      });
  };

  //handleDelete() function checks groupIDs for selected crises and if groupID exists, then it triggers DRAW action to fetch the group details and show confirmation popup.
  const handleDelete = (selection, props) => {
    setHasLoaded(false);
    const crisisListWithGroupId = [];
    let indexes = selection.map((id) =>
      crisisList.findIndex((element) => element.crisisID === id)
    );
    indexes.forEach((index) => {
      handleExpandChange('panel' + index)(null, false);
      const crisis = crisisList[index];
      if (crisis.groupID && crisis.groupID.length !== 0) {
        crisisListWithGroupId.push(crisis);
      }
    });
    if (crisisListWithGroupId.length > 0) {
      props.context.set('selectedCrisisWithGroupId', crisisListWithGroupId);
      props.context.trigger('Fetch Group and planner details');
    } else {
      handleDeleteCrisis(selection);
      setHasLoaded(true);
    }
  };

  const handleExpandChange = (panel) => (event, isExpanded) => {
    setHasLoaded(false);
    setExpanded(isExpanded ? panel : false);
    setCrisisId(crisisList[+panel.match(/\d+/g)].crisisID);
    setHasLoaded(true);
    event && event.stopPropagation();
  };

  const getCrisisList = async () => {
    if (typeof isAdmin === "boolean") {
      return await getBulkCrisis(isAdmin, graphToken);
    }
    else return { error: "Error" };
  };

  useEffect(() => {
    props.boolReload.subscribe(() => {
      setHasLoaded(false);
      getCrisisList().then((data) => {
        if (!data.error) {
          setFilteredCrisisList(data);
          setCrisisList(data);
          setCheckedState(new Array(data.length).fill(false));
        }
      });
      setHasLoaded(true);
    });
  }, [isAdmin]);

//Fetch loggedIn user all group membership data By 'memberOf' Graph API
  useEffect(()=>{
    if(crisisList != undefined && crisisList.length != 0){           
      getUserMemberGroups(graphToken).then((data)=>{
        const groupdata = data.map(item=> item.id)
        setMemberGroupList(groupdata);
      })
    }
  },[crisisList])

  useEffect(() => {
    props.context.trigger('Check if user is admin');
  }, []);

  useEffect(() => {
    setHasLoaded(false);
    getCrisisList().then((data) => {
      if (!data.error) {
        setFilteredCrisisList(data);
        setCrisisList(data);
        setCheckedState(new Array(data.length).fill(false));
      }
    });
    setHasLoaded(true);
  }, [refresh]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (selectedCrisis.length === 0) {
      props.throwError(null, 'Please select at least 1 crisis to delete');
    } else {
      handleDelete(selectedCrisis, props);
    }
  }, [props.crisisDeleted]);
  // Task planner button event
  const handleTaskPlanner = (event, props, crisis) => {
    props.context.set('selected Crisis', crisis);
    props.context.trigger('Go to Planner app');
    event.stopPropagation();
  };

  useEffect(() => {
    setFilteredCrisisList(filterByValue(crisisList, props.searchText));
  }, [props.searchText]);

  useEffect(() => {
    setHasLoaded(false);
    const updatedCheckedState = checkedState.map((item, idx) => {
      return checkedState[idx] !== props.isSelectedAll ? !item : item;
    });
    props.isSelectedAll
      ? setSelectedCrisis(crisisList.map((item) => item.crisisID))
      : setSelectedCrisis([]);
    setCheckedState(updatedCheckedState);
    setHasLoaded(true);
  }, [props.isSelectedAll]);
  // Create task event
  const handleCreateTask = (event, props, crisis) => {
    props.context.set('selected Crisis', crisis);
    props.context.trigger('Create Task');
    event.stopPropagation();
  };
  // Create Group and planner event
  const handleCreateGroup = (event, props, crisis) => {
    props.context.trigger('Create group');
    props.context.set('selected Crisis', crisis);
    event.stopPropagation();
  };

  useEffect(() => {
    if (deleteFromDB) {
      setHasLoaded(false);
      handleDeleteCrisis(selectedCrisis);
      setSelectedCrisis([]);
      setHasLoaded(true);
      props.context.set('deleteFromDB', false);
    }
  }, [deleteFromDB]);

  return (
    <ThemeProvider theme={_theme}>
      <div style={{ overflow: 'scroll', width: '100%', height: '100%' }}>
        {hasLoaded &&
          crisisList.map((crisis, idx) => {
            if (filteredCrisisList.includes(crisis)) {
              return (
                // CANNOT add 'key' as of now for this child component as this breaks application behavior (search, delete etc).
                <InnerAccordion
                  crisisId={crisisId}
                  crisis={crisis}
                  idx={idx}
                  expanded={expanded}
                  checkedState={checkedState}
                  graphToken={graphToken}
                  handleExpandChange={handleExpandChange}
                  handleOnChange={handleOnChange}
                  handleCreateTask={handleCreateTask}
                  handleCreateGroup={handleCreateGroup}
                  handleTaskPlanner={handleTaskPlanner}
                  setHasLoaded={setHasLoaded}
                  getCrisisList={getCrisisList}
                  setCheckedState={setCheckedState}
                  setCrisisList={setCrisisList}
                  setFilteredCrisisList={setFilteredCrisisList}
                  props={props}                  
                  memberGroupsList ={memberGroupsList}
                />
              );
            }
          })}
      </div>
      {showSuccessErrorPopup && (
        <Popup
          title={errorMessage.length !== 0 ? `Error!` : 'Success!'}
          text={`${errorMessage.length !== 0
            ? `Could not delete selected ${selectedCrisis.length > 1 ? 'crises' : 'crisis'
            }: ${errorMessage}`
            : `Selected ${selectedCrisis.length > 1 ? 'crises were' : 'crisis was'
            } deleted`
            }`}
          type={errorMessage.length !== 0 ? 'Error' : 'Success'}
          onClose={() => {
            setShowSuccessErrorPopup(false);
            props.context.trigger('Close delete popup');
          }}
        />
      )}
    </ThemeProvider>
  );
};

registerBrick('017e7c0fdc70536b7b40', RCMonitorAccordion);
